import clsx from "clsx";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { InfoTooltip } from "@/ui/molecules/InfoTooltip";
import { ReactNode } from "react";
import { useAtomValue } from "jotai";
import {
    asOfDateAtom,
    availableDataRangeAtom,
} from "@/features/Metrics/state/Metrics.state";

interface DetailGridProps {
    title: string;
    showAsOfDate?: boolean;
    className?: string;
    eyebrow?: string;
    children: ReactNode | ReactNode[];
}

export function DetailGrid({
    title,
    className,
    eyebrow,
    children,
    showAsOfDate = false,
}: DetailGridProps) {
    const availableDateRange = useAtomValue(availableDataRangeAtom);
    const asOfDate = useAtomValue(asOfDateAtom);

    if (availableDateRange.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className={clsx("w-full", className)}>
            <div className="flex items-center justify-between">
                <h4 className="heading-m mb-3">{title}</h4>

                {eyebrow && (
                    <div className="tt-trigger group">
                        <CalendarDaysIcon className="size-4 stroke-gray-400" />
                        <p className="body-micro text-gray-400">{eyebrow}</p>
                        <div className="tt-container tt-bottom-custom">
                            <div className="tt w-48">{eyebrow}</div>
                        </div>
                    </div>
                )}
            </div>
            {showAsOfDate && asOfDate && (
                <div
                    className={clsx(
                        "mb-3 mt-2",
                        "body-s flex items-center gap-1",
                    )}
                >
                    <span className="subdued italic">
                        Data as of {asOfDate.format("MMM D, YYYY")}
                    </span>
                    <InfoTooltip>
                        The data displayed is from the last closed, complete
                        calendar period available in the company&apos;s
                        accounting system. Hum does not display partial period
                        data.
                    </InfoTooltip>
                </div>
            )}
            <div
                className={clsx(
                    "grid grid-cols-1",
                    "gap-0 overflow-hidden",
                    "sm:grid-cols-2 sm:gap-x-4",
                )}
            >
                {(Array.isArray(children) ? children : [children]).map(
                    (child: ReactNode, index: number) => {
                        const rowInGrid = Math.floor(index / 2);
                        const isEvenRow = rowInGrid % 2 === 0;
                        return (
                            <div
                                key={index}
                                className={clsx(
                                    "py-3, flex cursor-pointer items-center justify-between gap-4 border-x border-slate-700 px-4 hover:opacity-90",
                                    isEvenRow ? "bg-[#2E3849]" : "bg-[#353E4F]",
                                    index === 0 &&
                                        "rounded-t border-x border-t border-slate-700",
                                    index === 1 &&
                                        "border-x border-t border-slate-700 sm:rounded-t",
                                    index ===
                                        (Array.isArray(children)
                                            ? children.length
                                            : 1) -
                                            1 &&
                                        "rounded-b border-x border-b border-slate-700",
                                    index ===
                                        (Array.isArray(children)
                                            ? children.length
                                            : 1) -
                                            2 &&
                                        "border-x border-b border-slate-700 sm:rounded-b",
                                )}
                            >
                                {child}
                            </div>
                        );
                    },
                )}
            </div>
        </div>
    );
}
